import React, { Dispatch, Key, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {ReceiptViewGroupedByScope} from '../../../domain/Receipt/ReceiptView'
import IconFactory from '../svg/IconFactory'
import FetchFolderGateway from '../../../gateway/Folder/FetchFolderGateway'
import GetDetailAndStoreItUseCase from '../../../useCase/Folder/GetDetailAndStoreItUseCase'
import LocalStorageGateway from '../../../gateway/Form/LocalStorageGateway'
import {ErrorResponse} from '../../../domain/Api/Type'
import ErrorModal from '../modal/ErrorModal';
import { SignedDocument } from '../../../domain/Docusign/SignedDocument';
import SignDocument from '../validation/SignDocument';
import Spinner from '../svg/Spinner';
import { KYCControlGroupedByScope } from '../../../domain/Folder/KYCControl';
import { SubscriberScopeEnum } from '../../../domain/CustomerInfoRecall/SubscriberScopeEnum';
import { Tab, Tabs } from '../tab/Tab';
import ReceiptsValidationTab from './ReceiptsValidationTab';
import { SubscriberGroupedByScope } from '../../../domain/CustomerInfoRecall/CustomerInfoRecallGatewayInterface';

interface Props {
  dossierId: string
  receipts: ReceiptViewGroupedByScope
  documents : SignedDocument[]|null
  kyc: KYCControlGroupedByScope|null
  scope: string
  setScope: Dispatch<SetStateAction<string>>
  subscribers: SubscriberGroupedByScope | null
}

const ReceiptsValidation: React.FC<Props> = ({dossierId, receipts, documents, kyc, scope, setScope, subscribers}) => {
  const {t} = useTranslation()
  const [isDownloading, setDownloading] = useState<boolean>(false);
  const [previewReport, setPreviewReport] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string|null>(null)
  const allScopes = [SubscriberScopeEnum.NP, SubscriberScopeEnum.NP_COSOUS, SubscriberScopeEnum.USU, SubscriberScopeEnum.USU_COSOUS, SubscriberScopeEnum.UNDEFINED] as const
  const filteredScopes = allScopes.filter(scope => Object.prototype.hasOwnProperty.call(receipts, scope));

  const onTabChange = (index: number, key: Key|null) => {
    if (key) {
      setScope(filteredScopes[index])
    }
  };

  const downloadAllDocuments = () => {
    if(isDownloading)
      return
    setDownloading(true)
    const getDetailAndStoreItUseCase = new GetDetailAndStoreItUseCase(new FetchFolderGateway(), new LocalStorageGateway())

    getDetailAndStoreItUseCase?.downloadSignedDocument(dossierId)?.then((response) => {
      if (response !== null) {
        const url = URL.createObjectURL(response)
        const a = document.createElement('a')
        document.body.appendChild(a)
        a.href = url
        a.download = `dossier-${dossierId}.zip`
        a.click()
        window.URL.revokeObjectURL(url)
      }
    })
      .catch((errors: ErrorResponse) => {
        if (errors.errors?.length > 0) {
          setErrorMessage(errors.errors[0].message);
        } else {
          setErrorMessage(t(`docusign.unexpected_error`));
        }
      })
      .finally(() =>
        setDownloading(false)
      )
  }

  const getDownloadCaption = isDownloading ? t('button.download-in-progress') : t('button.download-document')
  const downloadDocWidget = (null !== documents && documents?.length > 0) ? (
    <div className="button-preview button-preview--reverse button-preview-download"
         onClick={() => downloadAllDocuments()}
    >
      <IconFactory type="download"/>
      <span className={`u-prs u-pxs`}>{getDownloadCaption}</span>
    </div>
  ) : ( (null !== documents ) ? <Spinner /> : (<span className="u-txt-italic">{t('docusign.documents_not_available')}</span>))

  const getValidSubscriberScope = (relatedScope: string) => {
    if (subscribers && Object.keys(subscribers).length === 1) {
      return SubscriberScopeEnum.NP
    }
    return relatedScope
  }

  return (
    <>
      { errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage(null)} /> }
      <div
        className={`box-elevations box-elevations--base box-elevations--mb u-mxAuto`}>
        <div className="flex-container">

          <h2 className="help-wrapper u-mbs">
            {t('validation.receipts-signed')}
          </h2>
          {documents?.map((document, index) => (
              <div key={document.id+'-'+index} className="col-md-12">
                <SignDocument folderId={dossierId} document={document}/>
              </div>
            ))}
        </div>
        {downloadDocWidget}

        <div className="flex-container">
          <h2 className="help-wrapper u-mbs u-mtl col-md-12">
            {t('validation.receipts')}
          </h2>
          <Tabs classes="wrap u-mxAuto" tabButtonClass="link-tab" tabClass={`link-tab--${filteredScopes.length}`} onTabChange={onTabChange} active={SubscriberScopeEnum.NP === scope ? 0 : 1}>
            {filteredScopes.map((item) => {
              return <Tab key={`receipt-${item}`} label={t(`validation.subscriber.${item}`)}>
                <ReceiptsValidationTab
                  dossierId={dossierId}
                  receipts={receipts[item]}
                  documents={documents}
                  kyc={kyc?.[item] ?? null}
                  scope={getValidSubscriberScope(item)}
                  subscribers={subscribers}
                />
              </Tab>
            })}
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default ReceiptsValidation
